import React, { useEffect, useState } from 'react';

import { Button, Modal } from '@/components';
import { Field } from '@/design-system/field';
import { Hint } from '@/design-system/hint';
import { Label } from '@/design-system/label';
import { PasswordInput } from '@/design-system/password-input';
import { toast } from '@/design-system/toaster';

import { ProviderUser, updateProviderUser } from '@/services/api/providers';

import {
  PASSWORD_RULE_HINT,
  validateConfirmPassword,
  validatePassword,
} from './providerUserForm';

interface ChangeUserPasswordModalProps {
  isOpen: boolean;
  providerId: number | string;
  user: ProviderUser | null;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Admin reset of a client-portal user's password — `PATCH …/users/:id` with
 * `{ password }`, as next-backoffice-web does. That route doesn't run core's
 * password-strength check, so the rule is enforced here.
 */
export const ChangeUserPasswordModal: React.FC<
  ChangeUserPasswordModalProps
> = ({ isOpen, providerId, user, onClose, onSaved }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setPassword('');
    setConfirmPassword('');
    setSubmitted(false);
  }, [isOpen]);

  const passwordError =
    submitted || password ? validatePassword(password) : '';
  const confirmPasswordError =
    submitted || confirmPassword
      ? validateConfirmPassword(password, confirmPassword)
      : '';

  const handleSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
    e?.preventDefault();
    setSubmitted(true);
    if (
      !user ||
      isSubmitting ||
      validatePassword(password) ||
      validateConfirmPassword(password, confirmPassword)
    ) {
      return;
    }

    setIsSubmitting(true);
    try {
      await updateProviderUser(providerId, user.id, { password });
      toast.success('Password updated successfully');
      onSaved();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to change password');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      icon="ri-key-line"
      iconTone="warning"
      title="Change User Password"
      subtitle={user ? `Reset password for ${user.name}` : undefined}
      width="sm"
      closeOnBackdropClick={!isSubmitting}
      closeOnEscape={!isSubmitting}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="white" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={() => handleSubmit()}
            disabled={isSubmitting}
            data-testid="provider-user-password-submit"
          >
            {isSubmitting ? 'Updating…' : 'Update Password'}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        <Field>
          <Label htmlFor="provider-user-new-password" required>
            New Password
          </Label>
          <PasswordInput
            id="provider-user-new-password"
            size="md"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            invalid={Boolean(passwordError)}
            autoComplete="new-password"
            showLeadingIcon={false}
          />
          {passwordError ? (
            <Hint tone="error">{passwordError}</Hint>
          ) : (
            <Hint>{PASSWORD_RULE_HINT}</Hint>
          )}
        </Field>

        <Field>
          <Label htmlFor="provider-user-new-password-confirm" required>
            Confirm Password
          </Label>
          <PasswordInput
            id="provider-user-new-password-confirm"
            size="md"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            invalid={Boolean(confirmPasswordError)}
            autoComplete="new-password"
            showLeadingIcon={false}
          />
          {confirmPasswordError && (
            <Hint tone="error">{confirmPasswordError}</Hint>
          )}
        </Field>

        <button type="submit" hidden aria-hidden tabIndex={-1} />
      </form>
    </Modal>
  );
};

export default ChangeUserPasswordModal;
